import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { TrendingUpIcon, TrendingDownIcon, ArrowRightIcon } from 'lucide-react';
interface Scenario {
  name: string;
  adSpend: number;
  cpc: number;
  clicks: number;
  ctlRate: number;
  leads: number;
  cpl: number;
  ltbRate: number;
  bookings: number;
  cpb: number;
}
const scenarios: Record<'conservative' | 'optimistic', Scenario> = {
  conservative: {
    name: 'Conservative',
    adSpend: 20000,
    cpc: 8,
    clicks: 2500,
    ctlRate: 5,
    leads: 125,
    cpl: 160,
    ltbRate: 30,
    bookings: 38,
    cpb: 526
  },
  optimistic: {
    name: 'Optimistic',
    adSpend: 20000,
    cpc: 5,
    clicks: 4000,
    ctlRate: 8,
    leads: 320,
    cpl: 63,
    ltbRate: 50,
    bookings: 160,
    cpb: 125
  }
};
function AnimatedNumber({
  value,
  prefix = '',
  suffix = '',
  duration = 1
}: {
  value: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
}) {
  const [displayValue, setDisplayValue] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (!isInView) return;
    let startTime: number;
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
      setDisplayValue(Math.floor(progress * value));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [isInView, value, duration]);
  return <span ref={ref} data-id="element-152">
      {prefix}
      {displayValue.toLocaleString()}
      {suffix}
    </span>;
}
export function PerformanceProjections() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const [activeScenario, setActiveScenario] = useState<'conservative' | 'optimistic'>('conservative');
  const scenario = scenarios[activeScenario];
  const funnelSteps = [{
    label: 'Ad Spend',
    value: scenario.adSpend,
    format: '$',
    color: '#1B2A4A'
  }, {
    label: 'Clicks',
    value: scenario.clicks,
    format: '',
    color: '#2A3F6A'
  }, {
    label: 'Leads',
    value: scenario.leads,
    format: '',
    color: '#C9A96E'
  }, {
    label: 'Bookings',
    value: scenario.bookings,
    format: '',
    color: '#8B7355'
  }];
  return <section id="projections" className="py-24 md:py-32 bg-white" ref={ref} data-id="element-153">
      <div className="max-w-6xl mx-auto px-6" data-id="element-154">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-12" data-id="element-155">
          <p className="text-[#C9A96E] text-sm font-semibold tracking-[0.2em] uppercase mb-3" data-id="element-156">
            Projections
          </p>
          <h2 className="text-3xl md:text-4xl font-light text-[#1B2A4A]" data-id="element-157">
            What You Can Expect
          </h2>
          <p className="text-[#666] mt-4 max-w-2xl mx-auto" data-id="element-158">
            Based on industry benchmarks for med spa Google Ads in competitive
            California markets.
          </p>
        </motion.div>

        {/* Scenario Toggle */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="flex justify-center mb-12" data-id="element-159">
          <div className="bg-[#F8F9FA] rounded-full p-1.5 inline-flex" data-id="element-160">
            <button onClick={() => setActiveScenario('conservative')} className={`px-6 py-3 rounded-full text-sm font-medium transition-all ${activeScenario === 'conservative' ? 'bg-[#1B2A4A] text-white shadow-md' : 'text-[#666] hover:text-[#1B2A4A]'}`} data-id="element-161">
              <span className="flex items-center gap-2" data-id="element-162">
                <TrendingDownIcon className="w-4 h-4" data-id="element-163" />
                Conservative
              </span>
            </button>
            <button onClick={() => setActiveScenario('optimistic')} className={`px-6 py-3 rounded-full text-sm font-medium transition-all ${activeScenario === 'optimistic' ? 'bg-[#C9A96E] text-white shadow-md' : 'text-[#666] hover:text-[#1B2A4A]'}`} data-id="element-164">
              <span className="flex items-center gap-2" data-id="element-165">
                <TrendingUpIcon className="w-4 h-4" data-id="element-166" />
                Optimistic
              </span>
            </button>
          </div>
        </motion.div>

        {/* Funnel Visualization */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="bg-[#F8F9FA] rounded-3xl p-8 md:p-12 mb-12" data-id="element-167">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-0" data-id="element-168">
            {funnelSteps.map((step, index) => <div key={step.label} className="flex items-center" data-id="element-169">
                <motion.div key={`${activeScenario}-${step.label}`} initial={{
              scale: 0.8,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              duration: 0.4,
              delay: index * 0.1
            }} className="text-center" data-id="element-170">
                  <div className="w-24 h-24 md:w-28 md:h-28 rounded-2xl flex flex-col items-center justify-center mb-3" style={{
                backgroundColor: `${step.color}15`
              }} data-id="element-171">
                    <p className="text-2xl md:text-3xl font-bold" style={{
                  color: step.color
                }} data-id="element-172">
                      <AnimatedNumber value={step.value} prefix={step.format} duration={0.8} data-id="element-173" />
                    </p>
                  </div>
                  <p className="text-sm text-[#666] font-medium" data-id="element-174">
                    {step.label}
                  </p>
                </motion.div>
                {index < funnelSteps.length - 1 && <ArrowRightIcon className="w-6 h-6 text-[#A8B4C4] mx-4 hidden md:block" data-id="element-175" />}
              </div>)}
          </div>
        </motion.div>

        {/* Metrics Grid */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12" data-id="element-176">
          <MetricCard label="Avg Cost Per Click" value={`$${scenario.cpc}`} isInView={isInView} delay={0.5} data-id="element-177" />
          <MetricCard label="Click-to-Lead Rate" value={`${scenario.ctlRate}%`} isInView={isInView} delay={0.55} data-id="element-178" />
          <MetricCard label="Cost Per Lead" value={`$${scenario.cpl}`} isInView={isInView} delay={0.6} data-id="element-179" />
          <MetricCard label="Lead-to-Booking Rate" value={`${scenario.ltbRate}%`} isInView={isInView} delay={0.65} data-id="element-180" />
        </motion.div>

        {/* Key Metrics Highlight */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.5
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-181">
          <div className="bg-gradient-to-br from-[#1B2A4A] to-[#2A3F6A] rounded-2xl p-8 text-white" data-id="element-182">
            <p className="text-white/70 text-sm mb-2" data-id="element-183">Monthly Bookings</p>
            <p className="text-5xl font-light mb-4" data-id="element-184">
              <AnimatedNumber value={scenario.bookings} duration={1} data-id="element-185" />
            </p>
            <p className="text-white/70 text-sm" data-id="element-186">
              {activeScenario === 'conservative' ? 'First 30 days (cold-start campaign)' : 'After 60–90 days of optimization'}
            </p>
          </div>
          <div className="bg-gradient-to-br from-[#C9A96E] to-[#8B7355] rounded-2xl p-8 text-white" data-id="element-187">
            <p className="text-white/70 text-sm mb-2" data-id="element-188">Cost Per Booking</p>
            <p className="text-5xl font-light mb-4" data-id="element-189">
              $<AnimatedNumber value={scenario.cpb} duration={1} data-id="element-190" />
            </p>
            <p className="text-white/70 text-sm" data-id="element-191">
              {activeScenario === 'conservative' ? 'Initial acquisition cost' : 'Optimized acquisition cost'}
            </p>
          </div>
        </motion.div>

        <motion.p initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {}} transition={{
        duration: 0.6,
        delay: 0.7
      }} className="mt-8 text-sm text-[#666] text-center max-w-3xl mx-auto" data-id="element-192">
          The{' '}
          <span className="text-[#1B2A4A] font-medium" data-id="element-193">
            conservative scenario
          </span>{' '}
          reflects a cold-start campaign in the first 30 days. The{' '}
          <span className="text-[#C9A96E] font-medium" data-id="element-194">
            optimistic scenario
          </span>{' '}
          reflects an optimized campaign after 60–90 days of testing and
          refinement. With a strong new website and aggressive optimization, we
          expect to move toward the optimistic range within the first 2–3
          months.
        </motion.p>
      </div>
    </section>;
}
function MetricCard({
  label,
  value,
  isInView,
  delay
}: {
  label: string;
  value: string;
  isInView: boolean;
  delay: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.4,
    delay
  }} className="bg-white rounded-xl p-5 border border-[#1B2A4A]/5 shadow-sm" data-id="element-195">
      <p className="text-xs text-[#666] mb-1" data-id="element-196">{label}</p>
      <p className="text-xl font-semibold text-[#1B2A4A]" data-id="element-197">{value}</p>
    </motion.div>;
}